/**
 * Finish Account Step
 *
 * Third step in the booking flow - account creation and booking completion.
 *
 * Layout:
 * - Desktop: Two-column layout (booking details | form + actions)
 * - Mobile: Single column (booking details → form → actions)
 *
 * States:
 * - account state 'incomplete': Show "Finish your account" with account form
 * - any other account state: Show "Review booking" without account form
 *
 * @module pages/booking/steps
 */

import { needsAccountSetup } from '@butter/library/shared';
import { useState, useMemo } from "react";
import {
  useBooking,
  useBookingStep,
} from "~/services/internalApi/booking/context";
import { useCreateBooking, useBookingPricing } from "~/services/internalApi/booking";
import { getPaymentTimingChange, getPromotionRejection } from "~/lib/apiClient";
import type { User } from "~/services/auth/types";
import { butterToast } from "@butter/ui/patterns";
import { toQuotedFees } from "@lgc/database/types";
import { useClubQuery } from "~/services/internalApi/club/hooks/useClubQuery";
import { useAuth } from "~/services/auth";
import { formatTimeRange, createClubDateTimeFromSlot } from "@butter/library/datetime";
import { format, parseISO } from 'date-fns';
import { calculateDurationWithMidnight, resolvePaymentRequirement } from "@butter/library/pricing";
import { deriveBookingQuote } from "./bookingQuote";
import { CompleteAccountForm } from "../components/CompleteAccountForm";
import { EarlyBookingConfirmDialog } from "../components";
import { useEarlyBookingConfirm } from "../shared/useEarlyBookingConfirm";
import { BookingDetailsCard } from "@butter/ui/domains/booking";
import { PriceSummaryCard } from "@butter/ui/domains/billing";
import { RetryBanner } from "@butter/ui/components/feedback";
import { useTenantNavigate } from "~/lib/navigation";
import { useWaiverSubmission } from "../components/useWaiverSubmission";
import { waiverRefusalMessage } from "../components/waiverCompletion";
import { useWaiverGateHold } from "../../../features/waivers/waiverGateHold";

/**
 * Finish Account Step Component
 *
 * Layout varies based on the account state:
 * - 'incomplete' or 'pending': Shows account completion form
 * - otherwise: Shows review only (no form)
 */
export function FinishAccountStep() {
  const navigate = useTenantNavigate();
  const { user: authenticatedUser } = useAuth();
  const { data: club } = useClubQuery();

  const {
    selectedDate,
    selectedStartTime,
    selectedEndTime,
    selectedBayId,
    guestUser,
    paymentMethodToken,
    paymentProvider,
    clearPaymentData,
    guestCount,
    setGuestUser,
  } = useBooking();

  const { goToPreviousStep, promoCode, setPromoCode } = useBookingStep();

  const waiverGate = useWaiverSubmission(club?.id);
  // Account completion flips auth mid-step; hold the entry gate so it can't
  // swap the flow for a SignWaiversScreen while this step posts the captured
  // consent (see waiverGateHold.ts).
  useWaiverGateHold();

  // Early-morning (1-5 AM) times are easily misread as PM — ask once, right
  // before the account form's submit completes the account.
  const earlyConfirm = useEarlyBookingConfirm(selectedStartTime);

  /**
   * Post the captured signatures, or refuse to continue if any are missing.
   *
   * Both booking-creation paths in this file call it, so a guest cannot reach a
   * created booking without a signed waiver on record.
   */
  const submitWaiversIfRequired = async () => {
    // Runs after auth has flipped and still reads the pre-flip answer — see
    // useWaiverSubmission for why, and for what would break that.
    if (!waiverGate.satisfied) {
      throw new Error(waiverRefusalMessage(waiverGate.answerMissing));
    }
    await waiverGate.submitWaivers();
  };

  const [error, setError] = useState<string | null>(null);
  // Whether `error` is the promo auto-drop rather than a fault — it changes the
  // banner's `data-kind` and nothing else. This step is where a GUEST meets the
  // three per-user rules for the first time: the quote before an account exists
  // cannot check enrollment, new-customers-only or the per-customer cap, so a
  // refusal here is an ordinary outcome, not a race.
  const [promoDropped, setPromoDropped] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Determine current user (guest or authenticated)
  const currentUser = guestUser || authenticatedUser;

  /**
   * Gate for rendering CompleteAccountForm. `pending` is bundled with `incomplete`
   * because neither ever set a user-chosen password — see PaymentDetailsStep.tsx for
   * the full rationale. The form posts NO setupToken, so the `pending` half works only
   * while the TEMPORARY G23 window is open in the API.
   * @see /apps/api/src/services/auth/docs/KNOWN_GAPS.md G23, G8
   */
  const isIncompleteUser = currentUser ? needsAccountSetup(currentUser) : false;

  // Find selected bay from club data
  const selectedBay = useMemo(
    () => club?.bays?.find((bay) => bay.id === selectedBayId),
    [club?.bays, selectedBayId]
  );

  // Duration only. This step computes NO price: a booking is priced by the server
  // (`POST /bookings/quote`, no bearer token needed), which is the only reader that
  // can see a per-bay premium or the club's pricing mode. The local calculation this
  // replaced was wrong by exactly the premium on any bay that carried one, and its
  // failure path disabled Confirm even when the server had priced the booking fine.
  const durationMinutes = useMemo(() => {
    if (!selectedStartTime || !selectedEndTime) return 0;
    return calculateDurationWithMidnight(selectedStartTime, selectedEndTime);
  }, [selectedStartTime, selectedEndTime]);

  // The authoritative price, fetched WITHOUT a bearer token. This is the whole
  // reason `POST /bookings/quote` exists: this step runs before the account does,
  // so until it existed the only number available here was a browser calculation
  // that could not see a per-bay premium — and the card was taken against it.
  const quoteTimes = useMemo(() => {
    if (!club?.timezone || !selectedDate || !selectedStartTime || !selectedEndTime) return null;
    try {
      // The same conversion `createUTCTimestamp` does, inlined: this has to resolve
      // above the price aliases below, and that helper is declared further down.
      return {
        startTime: createClubDateTimeFromSlot(selectedDate, selectedStartTime, club.timezone).date.toISOString(),
        endTime: createClubDateTimeFromSlot(selectedDate, selectedEndTime, club.timezone).date.toISOString(),
      };
    } catch {
      return null;
    }
  }, [club?.timezone, selectedDate, selectedStartTime, selectedEndTime]);

  const pricing = useBookingPricing({
    clubId: club?.id ?? null,
    bayId: selectedBayId,
    startTime: quoteTimes?.startTime ?? null,
    endTime: quoteTimes?.endTime ?? null,
    guestCount,
    // Still the guest route here: `/users/incomplete` creates and enrols a row
    // but establishes no session, so this step quotes anonymously and the
    // per-user rules are met at reserve. A brand-new account has no redemption
    // history to fail on, which is why that is harmless.
    promoCode: promoCode ?? undefined,
  });
  // The canonical code the quote applied, or undefined. Off the SERVER's verdict,
  // and carrying the server's spelling of the code rather than what was typed.
  const promoCodeForPurchase = pricing.promotion?.applied ? pricing.promotion.code : undefined;

  // What the visitor SEES has to be what the card is charged. `paymentAmount`
  // below submits `pricing.totalCost.total`, so every figure rendered on this
  // step reads off that same quote.
  //
  // THREE states, not two — conflating the first two renders "$0.00" over a
  // booking the server refused, next to an enabled Confirm:
  //
  //   !hasQuote            the server has not answered → pending, no figures
  //   isValid === false    it answered by REFUSING (subscription-only club, bay
  //                        gate, or a pricing failure — all of which are 200s
  //                        carrying a payload, so `hasQuote` is true) → its own
  //                        reason, no card
  //   totalCost absent     it priced the booking at nothing → real $0.00, and
  //                        Confirm must stay enabled
  //
  // Both halves of `refused` are load-bearing, and the second is what keeps this
  // correct if the step's assumptions move. On the guest endpoint
  // `canProceedWithPayment` is absent for a booker with no plan
  // (`validateBookingTime` omits it), so `!undefined` is `true` and every refusal
  // stays a refusal. On `/bookings/validate` — which `useBookingPricing` switches
  // to the moment the caller is authenticated — `isValid: false` with
  // `canProceedWithPayment: true` is a real BOOKABLE shape: a member over their
  // allowance, who must be shown an overage price rather than a refusal. This step
  // is guest-only today (the advance is gated on `!isAuthenticated`), but that
  // invariant lives in another file, and reading `isValid` alone would turn a
  // change there into a member who silently cannot book. Same expression the
  // sibling step uses — one concept, one spelling.
  //
  // The GATE is `deriveBookingQuote` + `resolvePaymentRequirement` — the same pair
  // PaymentDetailsStep uses, so "is this priced?" has one implementation and one
  // set of tests rather than two that can drift. It deliberately collapses a
  // refusal into `pending`, which is right for gating and not enough for
  // rendering: the step still has to show WHY. Hence the separate `refused` flag
  // below, used only to choose what is displayed.
  const refused = pricing.isValid === false && !pricing.canProceedWithPayment;
  const bookingQuote = deriveBookingQuote({
    // A guest runs no subscription query, so they are settled by definition.
    subscriptionSettled: true,
    validationEnabled: pricing.enabled,
    isValidating: pricing.isLoading,
    validationFailed: pricing.isError,
    hasValidationData: pricing.hasQuote,
    finalTotal: pricing.totalCost?.total ?? 0,
    validationRefused: refused,
    paymentTiming: pricing.paymentTiming,
  });
  const paymentRequirement = resolvePaymentRequirement(bookingQuote);
  const priceUnknown = paymentRequirement === 'unknown';
  // The club collects at the counter, so the guest continued without a card.
  const payingAtClub = paymentRequirement === 'at-club';
  const quoted = refused ? undefined : pricing.totalCost;

  const displayLineItems = pricing.fees.map((f) => ({
    key: f.type,
    label: f.label,
    amountDollars: f.baseAmount,
  }));
  const subtotal = quoted?.baseAmount ?? 0;
  const tax = quoted?.taxAmount ?? 0;
  const taxRate = quoted?.taxRatePercent ?? 0;
  // Already fee-inclusive on the quote — never sized locally on top of a server total.
  const processingFee = quoted?.processingFee ?? 0;
  const processingFeeRate = quoted?.processingFeeRate ?? 0;
  const totalWithFee = quoted?.total ?? 0;

  // Format duration for display
  const formatDuration = (minutes: number): string => {
    if (minutes === 60) return "1 Hour";
    if (minutes > 60) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      if (mins === 0) return `${hours} ${hours === 1 ? "Hour" : "Hours"}`;
      return `${hours} ${hours === 1 ? "Hour" : "Hours"} ${mins} Min`;
    }
    return `${minutes} Min`;
  };

  // React Query mutations
  const createBooking = useCreateBooking({
    onSuccess: async (result) => {
      const { booking, accessControl, invoiceId, access } = result;
      console.log("[FinishAccountStep] Booking created:", booking.id);

      // Payment method was already saved in PaymentDetailsStep
      // Clear sensitive data and navigate to confirmation
      clearPaymentData();

      // Navigate with booking + access-control data in state (for fast load).
      // `pricing` is what makes the confirmation page's Guest Fee row render.
      // useTenantNavigate carries only tenant-handle + club-id onto the URL.
      navigate(`/booking/confirmed/${booking.id}`, {
        state: {
          booking,
          accessControl,
          access,
          invoiceId,
          paymentMethodId: paymentMethodToken, // For fetching last 4 digits
          pricing: {
            subtotal,
            tax,
            total: totalWithFee,
            taxRate,
            // Off the quote, like every other figure on this step. This used to be a
            // local `calculateGuestFee` over the club's config — the same policy the
            // server applies, resolved twice, which is exactly how two numbers drift.
            guestFee: pricing.fees.find((f) => f.type === 'guest_fee')?.baseAmount ?? 0,
          },
        }
      });
    },
    // The API answered `success: false`. A refused code is dropped and the
    // member re-confirms at list — clearing `promoCode` changes the pricing
    // query key, so the total they see next is the one they will be charged.
    onFailure: (result) => {
      const rejection = getPromotionRejection(result);
      const timingChange = getPaymentTimingChange(result);
      if (rejection) {
        setPromoCode(null);
        setPromoDropped(true);
        setError(
          `${rejection.message} The code was removed — you can complete your booking at the regular price.`,
        );
      } else if (timingChange) {
        // How the booking is paid changed since the quote. This step has no card form, so
        // send the member back to the payment step, which prices it again and asks for a
        // card or shows Due at Club. The account is already complete by now, so they
        // continue there as the signed-in member they are.
        setPromoDropped(false);
        void pricing.refetch();
        butterToast.error(timingChange);
        goToPreviousStep();
      } else {
        setPromoDropped(false);
        setError(result.message);
      }
      setIsProcessing(false);
    },

    onError: (error) => {
      setPromoDropped(false);
      setError(error.message);
      setIsProcessing(false);
    },
  });

  // Helper: Convert club date/time to UTC ISO string
  const createUTCTimestamp = (timeString: string): string => {
    if (!club?.timezone || !selectedDate) {
      throw new Error("Missing club timezone or selected date");
    }

    const dateStr = selectedDate;

    // Use datetime library to properly convert club time to UTC
    const result = createClubDateTimeFromSlot(
      dateStr,
      timeString,
      club.timezone
    );

    // Return as ISO string for API
    return result.date.toISOString();
  };


  // Handle account completion for NEW users (account state 'incomplete' or 'pending')
  const handleAccountComplete = async (completedUser: User) => {
    // The account now exists and is signed in. Adopt it in place of the incomplete guest,
    // so a retry on this step — after a refused booking — confirms as an existing user
    // instead of submitting the account form again ("User not found").
    setGuestUser(completedUser, true);
    try {
      setIsProcessing(true);
      setError(null);

      // Validate all required data
      if (!currentUser) {
        throw new Error("User not found. Please restart the booking process.");
      }
      if (
        !selectedDate ||
        !selectedStartTime ||
        !selectedEndTime ||
        !selectedBayId
      ) {
        throw new Error(
          "Missing booking selections. Please complete all steps."
        );
      }
      if (!payingAtClub && (!paymentMethodToken || !paymentProvider)) {
        throw new Error(
          "Payment method not found. Please go back and re-enter payment."
        );
      }
      if (!club) {
        throw new Error(
          "Club information not loaded. Please refresh the page."
        );
      }

      console.log("[FinishAccountStep] Account completed, creating booking...");

      // Note: Account setup already happened in CompleteAccountForm
      // Now we just need to create the booking

      // Convert userId to number (backend API expects number)
      const userId = typeof currentUser.id === 'string'
        ? parseInt(currentUser.id, 10)
        : currentUser.id;

      if (isNaN(userId)) {
        throw new Error('Invalid user ID. Please try logging in again.');
      }

      const startTime = createUTCTimestamp(selectedStartTime);
      const endTime = createUTCTimestamp(selectedEndTime);

      // The quote is already on screen — `POST /bookings/quote` needs no token, so
      // it resolved while the visitor was filling this form rather than after
      // login. Both guards therefore run BEFORE the account is committed and the
      // waiver is signed, which is the point: a refusal used to surface after the
      // user existed.
      if (pricing.isValid === false) {
        setError(pricing.errors[0] ?? 'This booking could not be completed.');
        setIsProcessing(false);
        return;
      }
      if (!pricing.hasQuote) {
        setError('We could not confirm the price for this booking. Please try again.');
        setIsProcessing(false);
        return;
      }
      const quotedFees = toQuotedFees(pricing.fees);
      const paymentAmount = pricing.totalCost?.total ?? 0;

      // The guest path signs here rather than on the payment step: the account
      // does not exist until CompleteAccountForm finishes, and a signature has
      // to attach to a user. The drawn signature has been travelling in booking
      // context since the payment step. Signing before `mutate` means a failure
      // aborts the booking rather than leaving one against an unsigned waiver.
      await submitWaiversIfRequired();

      // Create booking with payment (navigation happens in onSuccess)
      createBooking.mutate({
        clubId: club.id,
        bayId: selectedBayId,
        userId,
        startTime,
        endTime,
        ...(paymentMethodToken && !payingAtClub && { paymentMethodId: paymentMethodToken }),
        paymentAmount,
        guestCount,
        // Echo the quote back for server reconciliation — the full breakdown
        // (incl. any bay premium) when one resolved for this booker.
        quotedFees,
        ...(pricing.paymentTiming ? { quotedPaymentTiming: pricing.paymentTiming } : {}),
      // The purchase only ever sends a code the QUOTE applied — the rule both
      // subscription funnels follow. A typed-then-refused code costs nothing at
      // Confirm, and it is what lets the server treat ANY create-time refusal as
      // "this died between the price you saw and this click" and refuse rather
      // than book you at list.
      ...(promoCodeForPurchase ? { promoCode: promoCodeForPurchase } : {}),
      });
    } catch (err) {
      console.error("[FinishAccountStep] Failed to create booking:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to create booking. Please try again."
      );
      setIsProcessing(false);
    }
  };

  // Handle booking creation for an existing user — reached on a retry, once
  // handleAccountComplete has adopted the completed account in place of the guest.
  const handleExistingUserBooking = async () => {
    try {
      setIsProcessing(true);
      setError(null);

      // Validate all required data
      if (!currentUser) {
        throw new Error("User not found. Please restart the booking process.");
      }
      if (
        !selectedDate ||
        !selectedStartTime ||
        !selectedEndTime ||
        !selectedBayId
      ) {
        throw new Error(
          "Missing booking selections. Please complete all steps."
        );
      }
      if (!payingAtClub && (!paymentMethodToken || !paymentProvider)) {
        throw new Error(
          "Payment method not found. Please go back and re-enter payment."
        );
      }
      if (!club) {
        throw new Error(
          "Club information not loaded. Please refresh the page."
        );
      }

      console.log("[FinishAccountStep] Creating booking for existing user...");

      // Convert userId to number (backend API expects number)
      const userId = typeof currentUser.id === 'string'
        ? parseInt(currentUser.id, 10)
        : currentUser.id;

      if (isNaN(userId)) {
        throw new Error('Invalid user ID. Please try logging in again.');
      }

      const startTime = createUTCTimestamp(selectedStartTime);
      const endTime = createUTCTimestamp(selectedEndTime);

      // Same guards as handleAccountComplete's equivalent block.
      if (pricing.isValid === false) {
        setError(pricing.errors[0] ?? 'This booking could not be completed.');
        setIsProcessing(false);
        return;
      }
      if (!pricing.hasQuote) {
        setError('We could not confirm the price for this booking. Please try again.');
        setIsProcessing(false);
        return;
      }
      const quotedFees = toQuotedFees(pricing.fees);
      const paymentAmount = pricing.totalCost?.total ?? 0;

      // The guest path signs here rather than on the payment step: the account
      // does not exist until CompleteAccountForm finishes, and a signature has
      // to attach to a user. The drawn signature has been travelling in booking
      // context since the payment step. Signing before `mutate` means a failure
      // aborts the booking rather than leaving one against an unsigned waiver.
      await submitWaiversIfRequired();

      // Create booking with payment (navigation happens in onSuccess)
      createBooking.mutate({
        clubId: club.id,
        bayId: selectedBayId,
        userId,
        startTime,
        endTime,
        ...(paymentMethodToken && !payingAtClub && { paymentMethodId: paymentMethodToken }),
        paymentAmount,
        guestCount,
        // Echo the quote back for server reconciliation — the full breakdown
        // (incl. any bay premium) when one resolved for this booker.
        quotedFees,
        ...(pricing.paymentTiming ? { quotedPaymentTiming: pricing.paymentTiming } : {}),
      // The purchase only ever sends a code the QUOTE applied — the rule both
      // subscription funnels follow. A typed-then-refused code costs nothing at
      // Confirm, and it is what lets the server treat ANY create-time refusal as
      // "this died between the price you saw and this click" and refuse rather
      // than book you at list.
      ...(promoCodeForPurchase ? { promoCode: promoCodeForPurchase } : {}),
      });
    } catch (err) {
      console.error("[FinishAccountStep] Failed to create booking:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Failed to create booking. Please try again."
      );
      setIsProcessing(false);
    }
  };

  // Handle save and confirm button
  const handleSaveAndConfirm = () => {
    if (isIncompleteUser) {
      // For pending users, trigger account completion (form handles its own submission)
      // The form's onComplete will call handleAccountComplete
      return;
    } else {
      // For existing users, directly create booking
      handleExistingUserBooking();
    }
  };

  return (
    <div className="">
      {/* Error Message (if any) */}
      {error && (
        <div
        className={
          promoDropped
            ? "mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg"
            : "mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
        }
        role={promoDropped ? "status" : "alert"}
        aria-live="polite"
        data-testid="confirmation-error-message"
        data-kind={promoDropped ? "promotion_rejected" : "error"}
        >
          <p className={promoDropped ? "text-sm text-amber-800" : "text-sm text-red-800"}>{error}</p>
        </div>
      )}

      {/* Two Column Layout - Desktop | Single Column - Mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* LEFT COLUMN - Booking & Price Summary. No `order-*`: it is already first in
            the DOM, and reversing it on mobile pushed the price breakdown below the
            form and off-screen. */}
        <div className="space-y-6">
          {/* Booking Details Card */}
          {club && selectedDate && selectedStartTime && selectedEndTime && (
            <BookingDetailsCard
              imageUrl={selectedBay?.image?.url || null}
              imageAlt={selectedBay?.image?.alt}
              name={selectedBay?.name || "Bay"}
              bayId={selectedBayId || 0}
              date={format(parseISO(selectedDate), "EEEE, d MMMM, yyyy")}
              time={formatTimeRange(selectedStartTime, selectedEndTime)}
              duration={formatDuration(durationMinutes)}
            />
          )}

          {/* The quote could not be fetched at all (network / 5xx). Distinct from a
              refusal: there is nothing wrong with the booking, so offer a retry
              rather than a reason. Mirrors PaymentDetailsStep. */}
          {pricing.isError && (
            <RetryBanner
              message="We couldn't get a price for this booking."
              onRetry={() => void pricing.refetch()}
              testId="finish-quote-failed"
            />
          )}

          {/* This step renders no WaiverSection — the waiver was shown and signed
              two steps back — so a failed requirements query has nowhere else to
              surface. Without this the guest gets the honest refusal message from
              `waiverRefusalMessage` and no way to re-ask. */}
          {waiverGate.answerMissing && (
            <RetryBanner
              message="We couldn't confirm this club's waiver requirements."
              onRetry={() => void waiverGate.retry()}
              testId="finish-waiver-failed"
            />
          )}

          {pricing.promotion && !pricing.promotion.applied && (
            // The verdict, not the field. The code was entered on the review
            // step; a second input here would be a second place to hold it.
            <div
              className="rounded-lg border border-amber-200 bg-amber-50 p-3"
              role="status"
              data-testid="finish-promo-refused"
              data-reason={pricing.promotion.reason}
            >
              <p className="text-sm text-amber-800">
                {pricing.promotion.message ?? 'This promo code could not be applied.'}{' '}
                You can still complete your booking at the regular price.
              </p>
            </div>
          )}

          {/* Price Summary — or the server's refusal, which takes priority over
              it: there is no price to show, only the reason there isn't one. */}
          {refused && pricing.errors.length > 0 ? (
            <div
              className="rounded-2xl border border-red-200 bg-red-50 p-4"
              role="alert"
              data-testid="finish-validation-refused"
            >
              {pricing.errors.map((err, i) => (
                <p key={i} className="text-sm text-red-800">{err}</p>
              ))}
            </div>
          ) : (
          <PriceSummaryCard
            pending={priceUnknown}
            totalLabel={payingAtClub ? 'Due at Club' : undefined}
            subtotal={subtotal}
            tax={tax}
            total={totalWithFee}
            taxRatePercent={taxRate}
            processingFee={processingFee}
            processingFeeRate={processingFeeRate}
            lineItems={displayLineItems}
            // Disclosure only — every figure above is already net of it.
            subscriptionDiscount={pricing.totalCost?.discountTotal}
            discountLabel={pricing.totalCost?.discountLabel}
          />
          )}
        </div>

        {/* RIGHT COLUMN - Form & Actions */}
        <div className="space-y-6 p-4">
          {/* Complete Account Form - Only shown if the account state is 'pending'. It
              completes the account in its own onSubmit, before onComplete books —
              so the early-morning prompt holds the submit (button or Enter) here,
              before that handler runs. */}
          {isIncompleteUser && currentUser && (
            <div onSubmitCapture={earlyConfirm.captureSubmit}>
              <CompleteAccountForm
                userId={currentUser.id}
                phoneRegion={club?.country}
                email={currentUser.email}
                onComplete={handleAccountComplete}
                clubId={club?.id}
              />
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col text-sm sm:flex-row gap-3">
            {/* Back Button */}
            <button
              onClick={goToPreviousStep}
              disabled={isProcessing}
              data-testid="confirmation-back-button"
              className="flex-1 px-6 py-2 bg-white text-gray-700 border border-theme-600 rounded-full font-medium hover:bg-gray-50 focus:bg-gray-50  transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Back
            </button>

            {/* Save and Confirm Button */}
            {isIncompleteUser ? (
              <button
                type="submit"
                form="complete-account-form"
                // Also gated on the waiver: this form COMPLETES the account before
                // `onComplete` runs, so refusing afterwards leaves an account with no
                // booking. Safe to disable because a missing requirements list renders
                // the retry banner above. Matches DemoFinishStep.
                disabled={isProcessing || priceUnknown || !waiverGate.satisfied}
                data-testid="confirmation-form-submit-button"
                className="flex-1 px-6 py-2 bg-theme-600 text-white rounded-full font-semibold hover:bg-theme-700 focus:bg-theme-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? "Processing..." : "Save and Confirm"}
              </button>
            ) : (
              <button
                onClick={handleSaveAndConfirm}
                disabled={isProcessing || priceUnknown || !waiverGate.satisfied}
                data-testid="confirmation-form-submit-button"
                className="flex-1 px-6 py-2 bg-theme-600 text-white rounded-full font-semibold hover:bg-theme-700 focus:bg-theme-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? "Processing..." : "Confirm Booking"}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* No User Data: Error State */}
      {!currentUser && (
        <div className="rounded-lg border-2 border-yellow-200 bg-yellow-50 p-8">
          <div className="text-center">
            <p className="mb-4 text-yellow-800">
              ⚠️ Missing user information. Please restart the booking process.
            </p>
            <button
              onClick={goToPreviousStep}
              data-testid="confirmation-error-back-button"
              className="px-6 py-2 bg-theme-600 text-white rounded-lg font-medium hover:bg-theme-700"
            >
              Go Back
            </button>
          </div>
        </div>
      )}

      <EarlyBookingConfirmDialog
        open={earlyConfirm.open}
        startTime={selectedStartTime}
        endTime={selectedEndTime}
        onConfirm={earlyConfirm.confirm}
        onCancel={earlyConfirm.cancel}
      />
    </div>
  );
}
